import type { HTMLInputTypeAttribute, KeyboardEvent, Ref } from "react";

import styles from "./Field.module.css";

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: HTMLInputTypeAttribute;
  /** Quiet line under the field — read with it. */
  hint?: string | null;
  /** What is wrong, at the field (UX ruling 8): red, with the word, read with it. */
  error?: string | null;
  placeholder?: string;
  autoFocus?: boolean;
  /**
   * Shown, not editable — what was typed stays in view and can be selected,
   * but no longer changed (as `TextArea`). Not `disabled`: a disabled field
   * throws its focus to <body>.
   */
  readOnly?: boolean;
  max?: string;
  inputRef?: Ref<HTMLInputElement>;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * The one text field of this interface — the shape of `MoneyField` and
 * `SelectField` beside it (`Field.module.css`). An error replaces the hint,
 * marks the field `aria-invalid` and is its description, so a screen reader
 * says it the moment the field takes the focus.
 */
export function TextField({
  id,
  label,
  value,
  onChange,
  type = "text",
  hint,
  error,
  placeholder,
  autoFocus,
  readOnly = false,
  max,
  inputRef,
  onKeyDown,
}: TextFieldProps) {
  // Fix-Runde 1, M5: an error used to replace the hint outright — but a
  // hint can carry something the error doesn't (a format, a shortcut), and
  // hiding it right when a person needs it most served nobody. Both stand,
  // both are read (space-joined ids, the way `aria-describedby` takes more
  // than one).
  const describedBy = [error ? `${id}-error` : null, hint ? `${id}-hint` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={styles.field}>
      <label className={`label ${styles.fieldLabel}`} htmlFor={id}>
        {label}
      </label>
      <input
        ref={inputRef}
        id={id}
        type={type}
        className={`${styles.input} ${error ? styles.inputBad : ""} ${readOnly ? styles.inputReadOnly : ""}`}
        value={value}
        readOnly={readOnly}
        placeholder={placeholder}
        autoFocus={autoFocus}
        max={max}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
      />
      {error ? (
        <span id={`${id}-error`} className={`${styles.hint} ${styles.hintBad}`}>
          {error}
        </span>
      ) : null}
      {hint ? (
        <span id={`${id}-hint`} className={styles.hint}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
